// @flow
import * as React from 'react';
import {CountryCode} from 'libphonenumber-js/min';
import * as R from 'ramda';
import {
  makePhoneNumberFormatter,
  usePhoneNumberParser,
  validatePhoneNumber,
} from '@practicegenius/domain/src/data/phoneNumber';
import {Input} from './Input';

type InputProps = Omit<React.ComponentProps<typeof Input>, 'onChange'>;

type Props = InputProps & {
  onChange: (
    phoneNumber: string,
    // The form event with the form value replaced with the parsed value
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  countryCode?: CountryCode;
  onError?: (error: string | undefined) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export function PhoneNumberField({countryCode = 'US', ...props}: Props) {
  const [validationError, setValidationError] = React.useState<string>();
  const parse = usePhoneNumberParser(countryCode);
  const format = makePhoneNumberFormatter(countryCode);

  return (
    <Input
      {...props}
      value={format(props.value ?? '')}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange(
          parse(e.target.value),
          R.evolve(
            {target: {value: parse}},
            e
          ) as React.ChangeEvent<HTMLInputElement>
        );
      }}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        const error = validatePhoneNumber(countryCode, e.target.value);

        /**
         * This allows us to validate the phone number internally, while still allowing the consumers to know about it, if needed.
         */
        if (typeof props.onError === 'function') {
          props.onError(error);
        }

        /**
         * Some form libraries rely on their own "onBlur" functions being called
         * to work properly
         */
        if (props.onBlur) props.onBlur(e);

        setValidationError(error);
      }}
      id={`${props.name}_phone_number`}
      type="tel"
      // If this component needs to be controlled from the outside (like in the case of our RFF usages), we can do that, otherwise we just fall back to our internal validation.
      error={props.error ?? validationError}
    />
  );
}

/**
 * This is a light wrapper around PhoneNumberField that adheres to the standard format of `onChange` for text inputs,
 * so should be easier to use in Formik contexts
 */
export const PhoneNumberInput: React.FC<
  Omit<Props, 'onChange'> & {
    onChange: React.ChangeEventHandler<HTMLInputElement>;
  }
> = props => (
  <PhoneNumberField
    {...props}
    onChange={(value, event) => {
      props.onChange(event);
    }}
  />
);
